/**
 * Warn before leaving a page whose work would be lost. The app runs on a plain BrowserRouter
 * (main.tsx), not a data router, so react-router's useBlocker is not available: instead the
 * browser's own beforeunload prompt covers reload/close, and a capture-phase click listener on
 * the document asks before an in-app link (the Desk breadcrumb, the editorial tabs) navigates.
 * The browser Back button is not covered.
 */
import { useEffect, useRef } from 'react'

export type LinkClick = {
  /** The <a>'s resolved href (anchor.href), or null when the click was not on a link. */
  href: string | null
  target?: string | null
  download?: boolean
  button: number
  metaKey?: boolean
  ctrlKey?: boolean
  shiftKey?: boolean
  altKey?: boolean
  defaultPrevented?: boolean
}

/** Ask before following this click? Only a plain left click on a link to another page of this
 *  app; a new tab, a download, another site or the same page never asks. */
export function shouldWarnOnClick(active: boolean, c: LinkClick, here: string): boolean {
  if (!active || !c.href || c.defaultPrevented) return false
  if (c.button !== 0 || c.metaKey || c.ctrlKey || c.shiftKey || c.altKey) return false
  if (c.download || (c.target && c.target !== '_self')) return false
  let to: URL
  let from: URL
  try {
    from = new URL(here)
    to = new URL(c.href, from)
  } catch {
    return false
  }
  if (to.origin !== from.origin) return false
  return to.pathname !== from.pathname || to.search !== from.search
}

/** While `active`, ask `message` before an in-app link leaves the page, and have the browser
 *  ask before a reload or close (browsers show their own wording there). */
export function useLeaveWarning(active: boolean, message: string): void {
  const msg = useRef(message)
  useEffect(() => { msg.current = message }, [message])
  useEffect(() => {
    if (!active) return
    const onUnload = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = '' }
    const onClick = (e: MouseEvent) => {
      const a = e.target instanceof Element ? e.target.closest('a') : null
      const click: LinkClick = {
        href: a?.href ?? null, target: a?.getAttribute('target'), download: a?.hasAttribute('download') ?? false,
        button: e.button, metaKey: e.metaKey, ctrlKey: e.ctrlKey, shiftKey: e.shiftKey, altKey: e.altKey,
        defaultPrevented: e.defaultPrevented,
      }
      if (!shouldWarnOnClick(true, click, window.location.href)) return
      if (window.confirm(msg.current)) return
      // Stop it here, before React (and the router's <Link>) sees it.
      e.preventDefault()
      e.stopPropagation()
    }
    window.addEventListener('beforeunload', onUnload)
    document.addEventListener('click', onClick, true)
    return () => {
      window.removeEventListener('beforeunload', onUnload)
      document.removeEventListener('click', onClick, true)
    }
  }, [active])
}
